import { EINVOICE_PROVIDER_MODE, EINVOICE_SALE_TYPE, EINVOICE_STATUS } from '@/utils/einvoice.enum'
import { parseBillTypeItem, resolveSaleTypeByBillType } from './einvoice.contract'
import { createClientValidationError } from './einvoice.action-errors'

const STATUS_BY_KEY = Object.freeze({
  DRAFT: EINVOICE_STATUS.DRAFT,
  READY_TO_ISSUE: EINVOICE_STATUS.READY_TO_ISSUE,
  ISSUING: EINVOICE_STATUS.ISSUING,
  PENDING: EINVOICE_STATUS.ISSUING,
  ISSUED: EINVOICE_STATUS.ISSUED,
  TAX_PENDING: EINVOICE_STATUS.TAX_PENDING,
  TAX_ACCEPTED: EINVOICE_STATUS.TAX_ACCEPTED,
  TAX_REJECTED: EINVOICE_STATUS.TAX_REJECTED,
  CANCELED: EINVOICE_STATUS.CANCELED,
  CANCELLED: EINVOICE_STATUS.CANCELED,
  ADJUSTED: EINVOICE_STATUS.ADJUSTED,
  REPLACED: EINVOICE_STATUS.REPLACED,
  ISSUE_FAILED: EINVOICE_STATUS.ISSUE_FAILED,
  FAILED: EINVOICE_STATUS.ISSUE_FAILED,
})

const TAX_STATUS_BY_KEY = Object.freeze({
  ACCEPTED: 'TAX_ACCEPTED',
  TAX_ACCEPTED: 'TAX_ACCEPTED',
  REJECTED: 'TAX_REJECTED',
  TAX_REJECTED: 'TAX_REJECTED',
  PENDING: 'TAX_PENDING',
  TAX_PENDING: 'TAX_PENDING',
})

const BULK_PRECHECK_ACTION_MATRIX = Object.freeze({
  retail: new Set(['issue', 'query-status', 'cancel']),
  wholesale: new Set(['issue', 'query-status', 'query-tax-status', 'resend-email']),
})

const BULK_ISSUE_INVOICE_TYPES = new Set(['retail', 'wholesale'])

const BULK_ISSUE_PROGRESS_STATUS = Object.freeze({
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  UNKNOWN: 'UNKNOWN',
})

const BULK_ISSUE_PROGRESS_STATUS_ALIASES = Object.freeze({
  IN_PROGRESS: BULK_ISSUE_PROGRESS_STATUS.IN_PROGRESS,
  INPROGRESS: BULK_ISSUE_PROGRESS_STATUS.IN_PROGRESS,
  PROCESSING: BULK_ISSUE_PROGRESS_STATUS.IN_PROGRESS,
  PENDING: BULK_ISSUE_PROGRESS_STATUS.IN_PROGRESS,
  COMPLETED: BULK_ISSUE_PROGRESS_STATUS.COMPLETED,
  DONE: BULK_ISSUE_PROGRESS_STATUS.COMPLETED,
  SUCCESS: BULK_ISSUE_PROGRESS_STATUS.COMPLETED,
  SUCCEEDED: BULK_ISSUE_PROGRESS_STATUS.COMPLETED,
  FAILED: BULK_ISSUE_PROGRESS_STATUS.FAILED,
  FAIL: BULK_ISSUE_PROGRESS_STATUS.FAILED,
  ERROR: BULK_ISSUE_PROGRESS_STATUS.FAILED,
  UNKNOWN: BULK_ISSUE_PROGRESS_STATUS.UNKNOWN,
})

const ARTIFACT_STATES = new Set(['ready', 'cooldown', 'not_ready', 'failed', 'unknown'])
const CANONICAL_ITEM_COLLECTION_KEYS = Object.freeze(['items', 'lineItems', 'convertedItems'])
const CANONICAL_ITEM_MONEY_BREAKDOWN_FIELDS = Object.freeze([
  'grossBeforeDiscount',
  'grossAfterDiscount',
  'discountAmountBeforeTax',
  'netBeforeDiscount',
  'netAfterDiscount',
  'unitGrossBeforeDiscount',
  'unitGrossAfterDiscount',
  'unitNetBeforeDiscount',
  'unitNetAfterDiscount',
])

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '')
}

function normalizeNullableString(value) {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || null
  }

  const normalized = String(value).trim()
  return normalized || null
}

function normalizeNullableInteger(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isInteger(parsed) ? parsed : null
}

function normalizeSourceChannel(value) {
  return normalizeNullableString(value)
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeArtifactState(value) {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim().toLowerCase()
  if (!normalized) {
    return null
  }

  return ARTIFACT_STATES.has(normalized) ? normalized : 'unknown'
}

function normalizeRetryAfterSeconds(value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }

  return Math.ceil(parsed)
}

function normalizeImageUrl(...values) {
  for (const value of values) {
    if (typeof value !== 'string') {
      continue
    }

    const trimmed = value.trim()
    if (trimmed) {
      return trimmed
    }
  }

  return null
}


function requireCanonicalSummaryField(source, field, summaryPath) {
  if (!source || !Object.prototype.hasOwnProperty.call(source, field)) {
    throw createClientValidationError(
      'EINVOICE_CANONICAL_SUMMARY_FIELD_INVALID',
      `Canonical summary thiếu ${field} tại ${summaryPath}`,
      {
        summaryPath,
        field,
      },
    )
  }

  const normalizedValue = toNumber(source[field], Number.NaN)
  if (!Number.isFinite(normalizedValue)) {
    throw createClientValidationError(
      'EINVOICE_CANONICAL_SUMMARY_FIELD_INVALID',
      `Canonical summary có ${field} không hợp lệ tại ${summaryPath}`,
      {
        summaryPath,
        field,
        receivedValue: source[field],
      },
    )
  }

  return normalizedValue
}

function resolveCanonicalItemCollection({
  items,
  lineItems,
  convertedItems,
  source = null,
  fallbackItems = [],
} = {}) {
  if (Array.isArray(items)) {
    return {
      items,
      sourceKey: 'items',
    }
  }

  if (Array.isArray(lineItems)) {
    return {
      items: lineItems,
      sourceKey: 'lineItems',
    }
  }

  if (Array.isArray(convertedItems)) {
    return {
      items: convertedItems,
      sourceKey: 'convertedItems',
    }
  }

  if (source && typeof source === 'object') {
    for (const key of CANONICAL_ITEM_COLLECTION_KEYS) {
      if (Array.isArray(source[key])) {
        return {
          items: source[key],
          sourceKey: key,
        }
      }
    }
  }

  return {
    items: Array.isArray(fallbackItems) ? fallbackItems : [],
    sourceKey: 'fallbackItems',
  }
}

function normalizeStatus(status, fallback = EINVOICE_STATUS.DRAFT) {
  if (typeof status === 'number') {
    return status
  }

  if (typeof status !== 'string') {
    return fallback
  }

  const normalizedKey = status
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
  return STATUS_BY_KEY[normalizedKey] || fallback
}

function normalizeTaxStatus(taxStatus, status) {
  const normalizedSource = pickFirst(taxStatus, status)
  if (typeof normalizedSource !== 'string') {
    return null
  }

  const normalizedKey = normalizedSource
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
  return TAX_STATUS_BY_KEY[normalizedKey] || null
}

function normalizeBulkPrecheckInvoiceType(raw) {
  const normalized = normalizeNullableString(raw)
  return normalized ? normalized.toLowerCase() : null
}

function normalizeBulkPrecheckAction(raw) {
  const normalized = normalizeNullableString(raw)
  return normalized ? normalized.toLowerCase() : null
}

function normalizeBulkPrecheckEmail(raw) {
  const normalized = normalizeNullableString(raw)
  return normalized ? normalized.toLowerCase() : null
}

function normalizeBulkPrecheckIds(ids) {
  const source = Array.isArray(ids) ? ids : []
  const normalized = source.map((value) => normalizeNullableString(value)).filter(Boolean)

  return Array.from(new Set(normalized))
}

function normalizeBulkPrecheckItem(item = {}, kind = 'eligible') {
  const salesInvoiceId = normalizeNullableString(
    pickFirst(item.salesInvoiceId, item.id, item.invoiceId, item.billId, item.saleInvoiceId, null),
  )

  return {
    salesInvoiceId: salesInvoiceId || '',
    invoiceNo: pickFirst(item?.metadata?.billNumber, salesInvoiceId, ''),
    buyerName: pickFirst(item.buyerName, item.customerName, item.buyer?.buyerName, '') || '',
    totalAmount: toNumber(pickFirst(item.totalAmount, item.amount, item.grandTotal), 0),
    currentState: pickFirst(item.currentState, item.status, null),
    ...(kind === 'blocked'
      ? {
          code: normalizeNullableString(pickFirst(item.code, item.reasonCode, null)),
          message: pickFirst(item.message, item.reason, item.feedbackContent, ''),
        }
      : {}),
  }
}

function normalizeBulkPrecheckSummary(summary = {}, eligible = [], blocked = [], total = 0) {
  return {
    eligible: toNumber(pickFirst(summary?.eligible, eligible.length), eligible.length),
    blocked: toNumber(pickFirst(summary?.blocked, blocked.length), blocked.length),
    eligibleTotalAmount: toNumber(
      pickFirst(
        summary?.eligibleTotalAmount,
        eligible.reduce((sum, item) => sum + toNumber(item.totalAmount, 0), 0),
      ),
      0,
    ),
  }
}

function normalizeBulkPrecheckRequest(payload = {}) {
  const invoiceType = normalizeBulkPrecheckInvoiceType(payload.invoiceType)
  const action = normalizeBulkPrecheckAction(payload.action)
  const salesInvoiceIds = normalizeBulkPrecheckIds(payload.salesInvoiceIds)
  const email = normalizeBulkPrecheckEmail(payload.email)

  if (!invoiceType || !BULK_PRECHECK_ACTION_MATRIX[invoiceType]) {
    throw new Error('invoiceType phải là "retail" hoặc "wholesale"')
  }

  if (!action || !BULK_PRECHECK_ACTION_MATRIX[invoiceType].has(action)) {
    throw new Error(`Action "${action || ''}" không hợp lệ cho invoiceType "${invoiceType}"`)
  }

  if (!salesInvoiceIds.length) {
    throw new Error('Cần ít nhất 1 salesInvoiceId để kiểm tra trước phát hành')
  }

  if (salesInvoiceIds.length > 100) {
    throw new Error('Chỉ được kiểm tra tối đa 100 salesInvoiceId trong một lần')
  }

  if (action === 'resend-email') {
    if (!email) {
      throw new Error('Email là bắt buộc khi action là "resend-email"')
    }

    return {
      invoiceType,
      action,
      salesInvoiceIds,
      email,
    }
  }

  return {
    invoiceType,
    action,
    salesInvoiceIds,
  }
}

function normalizeBulkPrecheckResponse(payload = {}) {
  const source = payload?.data || payload || {}
  const eligible = Array.isArray(source.eligible)
    ? source.eligible.map((item) => normalizeBulkPrecheckItem(item, 'eligible'))
    : []
  const blocked = Array.isArray(source.blocked)
    ? source.blocked.map((item) => normalizeBulkPrecheckItem(item, 'blocked'))
    : []
  const total = toNumber(pickFirst(source.total, eligible.length + blocked.length), 0)

  return {
    action: normalizeBulkPrecheckAction(source.action) || null,
    invoiceType: normalizeBulkPrecheckInvoiceType(source.invoiceType) || null,
    total,
    eligible,
    blocked,
    summary: normalizeBulkPrecheckSummary(source.summary, eligible, blocked, total),
  }
}

function ensureBulkIssueInvoiceType(raw) {
  const invoiceType = normalizeBulkPrecheckInvoiceType(raw)
  if (!invoiceType || !BULK_ISSUE_INVOICE_TYPES.has(invoiceType)) {
    throw new Error('invoiceType phải là "retail" hoặc "wholesale"')
  }

  return invoiceType
}

function normalizeBulkIssueIdempotencyKey(raw) {
  const key = normalizeNullableString(raw)
  if (!key) {
    return null
  }

  if (key.length < 8 || key.length > 64) {
    throw new Error('Idempotency-Key phải có độ dài từ 8 đến 64 ký tự')
  }

  return key
}

function normalizeBulkIssueSubmitRequest(payload = {}) {
  const invoiceType = ensureBulkIssueInvoiceType(payload.invoiceType)
  const salesInvoiceIds = normalizeBulkPrecheckIds(payload.salesInvoiceIds)
  const idempotencyKey = normalizeBulkIssueIdempotencyKey(payload.idempotencyKey)

  if (!salesInvoiceIds.length) {
    throw new Error('Cần ít nhất 1 salesInvoiceId để phát hành hàng loạt')
  }

  if (salesInvoiceIds.length > 100) {
    throw new Error('Chỉ được phát hành tối đa 100 salesInvoiceId trong một lần')
  }

  return {
    invoiceType,
    salesInvoiceIds,
    ...(idempotencyKey ? { idempotencyKey } : {}),
  }
}

function buildBulkIssueSubmitFingerprint(payload = {}) {
  const normalized = normalizeBulkIssueSubmitRequest(payload)
  return `${normalized.invoiceType}:${normalized.salesInvoiceIds.join('|')}`
}

function normalizeBulkIssueSubmitResponse(payload = {}) {
  const source = payload?.data || payload || {}
  const jobId = normalizeNullableString(
    pickFirst(source.jobId, source.id, source.job?.jobId, source.job?.id, null),
  )

  if (!jobId) {
    throw new Error('Không nhận được jobId từ phản hồi phát hành hàng loạt')
  }

  return {
    jobId,
  }
}

function normalizeBulkIssueProgressStatus(rawStatus) {
  const normalized = normalizeNullableString(rawStatus)
  if (!normalized) {
    return BULK_ISSUE_PROGRESS_STATUS.UNKNOWN
  }

  const normalizedKey = normalized.toUpperCase().replace(/[\s-]+/g, '_')

  return BULK_ISSUE_PROGRESS_STATUS_ALIASES[normalizedKey] || BULK_ISSUE_PROGRESS_STATUS.UNKNOWN
}

function normalizeBulkIssueResultItem(item = {}, kind = 'succeeded') {
  const salesInvoiceId = normalizeNullableString(
    pickFirst(item.salesInvoiceId, item.id, item.invoiceId, item.billId, item.saleInvoiceId, null),
  )
  const code = normalizeNullableString(pickFirst(item.code, item.reasonCode, item.errorCode, null))
  const message = normalizeNullableString(
    pickFirst(item.message, item.reason, item.feedbackContent, item.errorMessage, null),
  )

  return {
    salesInvoiceId: salesInvoiceId || '',
    invoiceNo: pickFirst(item.invoiceNo, item.saleInvoiceCode, salesInvoiceId, ''),
    buyerName: pickFirst(item.buyerName, item.customerName, item.buyer?.buyerName, '') || '',
    totalAmount: toNumber(pickFirst(item.totalAmount, item.amount, item.grandTotal), 0),
    currentState: pickFirst(item.currentState, item.status) || null,
    ...(kind === 'failed'
      ? {
          code: code || null,
          reasonCode: code || null,
          message: message || '',
        }
      : {}),
  }
}

function normalizeBulkIssueTerminalResult(rawResult = null) {
  const source = rawResult && typeof rawResult === 'object' ? rawResult : {}
  const succeeded = Array.isArray(source.succeeded)
    ? source.succeeded.map((item) => normalizeBulkIssueResultItem(item, 'succeeded'))
    : []
  const failed = Array.isArray(source.failed)
    ? source.failed.map((item) => normalizeBulkIssueResultItem(item, 'failed'))
    : []
  const skippedSource = pickFirst(source.skipped, source.skippedCount, source.skipCount, 0)

  return {
    succeeded,
    failed,
    skipped: Array.isArray(skippedSource) ? skippedSource.length : toNumber(skippedSource, 0),
  }
}

function normalizeBulkIssueProgressResponse(payload = {}) {
  const source = payload?.data || payload || {}
  const status = normalizeBulkIssueProgressStatus(pickFirst(source.status, source.jobStatus, null))
  const terminalResult = normalizeBulkIssueTerminalResult(
    pickFirst(source.result, source.results, source.terminalResult, null),
  )

  return {
    jobId: normalizeNullableString(
      pickFirst(source.jobId, source.id, source.job?.jobId, source.job?.id, null),
    ),
    status,
    processed: toNumber(
      pickFirst(source.processed, source.completedCount, source.progress?.processed, 0),
      0,
    ),
    total: toNumber(
      pickFirst(source.total, source.totalCount, source.progress?.total, source.requested, 0),
      0,
    ),
    currentStep: normalizeNullableString(
      pickFirst(source.currentStep, source.step, source.progress?.currentStep, null),
    ),
    terminalResult,
    isTerminal:
      status === BULK_ISSUE_PROGRESS_STATUS.COMPLETED ||
      status === BULK_ISSUE_PROGRESS_STATUS.FAILED ||
      status === BULK_ISSUE_PROGRESS_STATUS.UNKNOWN,
  }
}

function normalizeSaleType(raw) {
  if (raw === EINVOICE_SALE_TYPE.RETAIL || raw === EINVOICE_SALE_TYPE.WHOLESALE) {
    return raw
  }

  const value = typeof raw === 'string' ? raw.trim().toUpperCase() : ''
  if (!value) {
    return null
  }

  if (value.includes('WHOLESALE') || value.includes('PORTAL') || value.includes('B2B')) {
    return EINVOICE_SALE_TYPE.WHOLESALE
  }

  if (value.includes('RETAIL') || value.includes('FAST_POS') || value.includes('POS')) {
    return EINVOICE_SALE_TYPE.RETAIL
  }

  return null
}

function normalizeProviderMode(raw) {
  const value = typeof raw === 'string' ? raw.trim().toUpperCase() : ''
  if (value.includes('PORTAL')) {
    return EINVOICE_PROVIDER_MODE.FAST_PORTAL
  }
  return EINVOICE_PROVIDER_MODE.FAST_POS
}

function normalizeTaxCode(raw) {
  if (typeof raw !== 'string') {
    return null
  }

  const normalized = raw.trim().toUpperCase()
  return normalized || null
}

function normalizeTaxBreakdownEntry(entry = {}) {
  const taxCode = normalizeTaxCode(pickFirst(entry.taxCode, entry.vatCode, null))
  return {
    taxRate: toNumber(pickFirst(entry.taxRate, entry.vatRate, entry.rate), 0),
    ...(taxCode ? { taxCode } : {}),
    totalBeforeTax: toNumber(
      pickFirst(
        entry.totalBeforeTax,
        entry.amount,
        entry.totalAmountBeforeTax,
        entry.subTotal,
        entry.totalNet,
      ),
      0,
    ),
    totalVat: toNumber(
      pickFirst(entry.totalVat, entry.vatAmount, entry.taxAmount, entry.totalTax),
      0,
    ),
  }
}

function deriveTaxBreakdown(items = []) {
  const grouped = items.reduce((result, item) => {
    const taxRate = toNumber(pickFirst(item.taxRate, 0), 0)
    const taxCode = normalizeTaxCode(pickFirst(item.taxCode, item.vatCode, null))
    const bucketKey = `${taxRate}::${taxCode || ''}`
    if (!result[bucketKey]) {
      result[bucketKey] = {
        taxRate,
        ...(taxCode ? { taxCode } : {}),
        totalBeforeTax: 0,
        totalVat: 0,
      }
    }

    result[bucketKey].totalBeforeTax += toNumber(pickFirst(item.totalBeforeTax, item.amount, 0), 0)
    result[bucketKey].totalVat += toNumber(pickFirst(item.taxAmount, 0), 0)
    return result
  }, {})

  return Object.values(grouped).sort((left, right) => {
    if (left.taxRate !== right.taxRate) {
      return left.taxRate - right.taxRate
    }

    return String(left.taxCode || '').localeCompare(String(right.taxCode || ''))
  })
}

/**
 * @typedef {Object} CanonicalItemMoneyBreakdown
 * @property {number} grossBeforeDiscount
 * @property {number} grossAfterDiscount
 * @property {number} discountAmountBeforeTax
 * @property {number} netBeforeDiscount
 * @property {number} netAfterDiscount
 * @property {number} unitGrossBeforeDiscount
 * @property {number} unitGrossAfterDiscount
 * @property {number} unitNetBeforeDiscount
 * @property {number} unitNetAfterDiscount
 */

/**
 * In canonical mode (`strictCanonicalMoneyBreakdown=true`) every item must provide a complete
 * `moneyBreakdown` object. Missing or invalid fields fail-closed with a client validation error.
 */
function normalizeLineItem(
  item = {},
  { strictCanonicalMoneyBreakdown = false, itemPath = 'items[]' } = {},
) {
  const quantity = toNumber(pickFirst(item.quantity, item.qty), 1)
  const grossAmount = toNumber(
    pickFirst(
      item.grossAmount,
      item.amountGross,
      item.lineGrossAmount,
      item.lineAmountBeforeDiscount,
      null,
    ),
    0,
  )
  const discountAmount = toNumber(pickFirst(item.discountAmount, item.discount), 0)
  const taxAmount = toNumber(pickFirst(item.taxAmount, item.vatAmount), 0)
  const explicitTotalBeforeTax = pickFirst(
    item.totalBeforeTax,
    item.amount,
    item.amountNet,
    item.lineAmount,
    item.subTotal,
  )
  const totalBeforeTax = toNumber(
    pickFirst(
      explicitTotalBeforeTax,
      grossAmount ? grossAmount - discountAmount : null,
      taxAmount === 0 ? item.totalAmount : null,
      null,
    ),
    0,
  )
  const inferredUnitPrice = toNumber(
    pickFirst(
      item.unitPrice,
      item.price,
      item.unit_amount,
      item.amountBeforeTax,
      quantity > 0 && grossAmount ? grossAmount / quantity : null,
    ),
    0,
  )
  const canonicalAmount = toNumber(
    pickFirst(item.amount, item.amountNet, item.lineAmount, item.subTotal, totalBeforeTax),
    totalBeforeTax,
  )
  const totalAmount = toNumber(
    pickFirst(item.totalAmount, item.total, totalBeforeTax + taxAmount),
    totalBeforeTax + taxAmount,
  )
  const imageUrl = normalizeImageUrl(item.imageUrl, item.productImageUrl)
  const fallbackGrossBeforeDiscount = toNumber(
    pickFirst(
      item.grossBeforeDiscount,
      item.amountGross,
      item.grossAmount,
      item.lineGrossAmount,
      item.lineAmountBeforeDiscount,
      grossAmount,
      totalAmount + discountAmount,
      0,
    ),
    0,
  )
  const fallbackGrossAfterDiscount = toNumber(
    pickFirst(
      item.grossAfterDiscount,
      item.totalAmount,
      item.amount,
      grossAmount ? grossAmount - discountAmount : null,
      totalAmount,
      0,
    ),
    0,
  )
  const fallbackNetAfterDiscount = toNumber(
    pickFirst(item.netAfterDiscount, item.totalBeforeTax, item.amountNet, totalBeforeTax, 0),
    0,
  )
  const fallbackUnitGrossBeforeDiscount = toNumber(
    pickFirst(
      item.unitGrossBeforeDiscount,
      quantity > 0 ? fallbackGrossBeforeDiscount / quantity : null,
      inferredUnitPrice,
      0,
    ),
    0,
  )
  const fallbackUnitGrossAfterDiscount = toNumber(
    pickFirst(
      item.unitGrossAfterDiscount,
      quantity > 0 ? fallbackGrossAfterDiscount / quantity : null,
      inferredUnitPrice,
      0,
    ),
    0,
  )
  const fallbackUnitNetAfterDiscount = toNumber(
    pickFirst(
      item.unitNetAfterDiscount,
      quantity > 0 ? fallbackNetAfterDiscount / quantity : null,
      0,
    ),
    0,
  )
  const fallbackMoneyBreakdown = {
    grossBeforeDiscount: fallbackGrossBeforeDiscount,
    grossAfterDiscount: fallbackGrossAfterDiscount,
    netAfterDiscount: fallbackNetAfterDiscount,
    unitGrossBeforeDiscount: fallbackUnitGrossBeforeDiscount,
    unitGrossAfterDiscount: fallbackUnitGrossAfterDiscount,
    unitNetAfterDiscount: fallbackUnitNetAfterDiscount,
  }
  const rawMoneyBreakdown =
    item?.moneyBreakdown &&
    typeof item.moneyBreakdown === 'object' &&
    !Array.isArray(item.moneyBreakdown)
      ? item.moneyBreakdown
      : null

  if (strictCanonicalMoneyBreakdown && !rawMoneyBreakdown) {
    throw createClientValidationError(
      'EINVOICE_CANONICAL_ITEM_MONEY_BREAKDOWN_REQUIRED',
      `Canonical item thiếu moneyBreakdown bắt buộc tại ${itemPath}`,
      {
        itemPath,
        lineNumber: pickFirst(item.lineNumber, item.lineId, item.id, null),
        itemCode: pickFirst(item.itemCode, item.productCode, item.code, item.sku, null),
      },
    )
  }

  const moneyBreakdown = CANONICAL_ITEM_MONEY_BREAKDOWN_FIELDS.reduce((result, field) => {
    const fallbackValue = fallbackMoneyBreakdown[field]
    if (!rawMoneyBreakdown || !Object.prototype.hasOwnProperty.call(rawMoneyBreakdown, field)) {
      if (strictCanonicalMoneyBreakdown) {
        throw createClientValidationError(
          'EINVOICE_CANONICAL_ITEM_MONEY_BREAKDOWN_FIELD_INVALID',
          `Canonical item thiếu moneyBreakdown.${field} tại ${itemPath}`,
          {
            itemPath,
            field,
            lineNumber: pickFirst(item.lineNumber, item.lineId, item.id, null),
            itemCode: pickFirst(item.itemCode, item.productCode, item.code, item.sku, null),
          },
        )
      }

      result[field] = fallbackValue
      return result
    }

    const normalizedValue = toNumber(rawMoneyBreakdown[field], Number.NaN)
    if (!Number.isFinite(normalizedValue)) {
      if (strictCanonicalMoneyBreakdown) {
        throw createClientValidationError(
          'EINVOICE_CANONICAL_ITEM_MONEY_BREAKDOWN_FIELD_INVALID',
          `Canonical item có moneyBreakdown.${field} không hợp lệ tại ${itemPath}`,
          {
            itemPath,
            field,
            lineNumber: pickFirst(item.lineNumber, item.lineId, item.id, null),
            itemCode: pickFirst(item.itemCode, item.productCode, item.code, item.sku, null),
            receivedValue: rawMoneyBreakdown[field],
          },
        )
      }

      result[field] = fallbackValue
      return result
    }

    result[field] = normalizedValue
    return result
  }, {})

  return {
    lineId: pickFirst(item.lineId, item.id, null),
    lineNumber: pickFirst(item.lineNumber, item.lineId, item.id, null),
    processType: pickFirst(item.processType, 1),
    productCode: pickFirst(item.productCode, item.itemCode, item.code, item.sku, null),
    itemCode: pickFirst(item.itemCode, item.productCode, item.code, item.sku, null),
    itemName: pickFirst(item.itemName, item.productName, item.name, item.description, ''),
    unitOfMeasure: pickFirst(item.unitOfMeasure, item.unit, item.unitName, item.uom, ''),
    quantity,
    unitPrice: inferredUnitPrice,
    amount: canonicalAmount,
    totalBeforeTax,
    discountAmount,
    discountRate: toNumber(pickFirst(item.discountRate, item.discountPercent), 0),
    taxRate: toNumber(pickFirst(item.taxRate, item.vatRate, item.taxPercent), 10),
    taxCode: normalizeTaxCode(pickFirst(item.taxCode, item.vatCode, null)),
    taxAmount,
    totalAmount,
    moneyBreakdown,
    isBonus: Boolean(item.isBonus),
    billItemId: item.billItemId ?? null,
    productId: pickFirst(item.productId, null),
    imageUrl,
  }
}

function normalizeRelation(item = {}) {
  return {
    relationType: pickFirst(item.relationType, item.type, item.relationshipType, ''),
    salesInvoiceId:
      pickFirst(
        item.salesInvoiceId,
        item.relatedSalesInvoiceId,
        item.saleInvoiceId,
        item.billId,
        null,
      ) || null,
    relatedInvoiceId: pickFirst(
      item.relatedInvoiceId,
      item.relatedId,
      item.invoiceId,
      item.id,
      null,
    ),
    invoiceNo: pickFirst(item.invoiceNo, item.relatedInvoiceNo, item.number, ''),
    invoiceDate: pickFirst(item.invoiceDate, item.date, item.relatedInvoiceDate, null),
  }
}

function normalizeAllowedActions(actions) {
  return Array.isArray(actions)
    ? actions
        .filter((action) => typeof action === 'string' && action.trim())
        .map((action) => action.trim())
    : []
}

function normalizeFieldSemanticValue(raw) {
  if (typeof raw !== 'string') {
    return null
  }

  const normalized = raw
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  return normalized || null
}

function normalizeFieldSemantics(raw) {
  if (!raw || typeof raw !== 'object') {
    return {}
  }

  return Object.keys(raw).reduce((result, key) => {
    const normalized = normalizeFieldSemanticValue(raw[key])
    if (normalized) {
      result[key] = normalized
    }
    return result
  }, {})
}

function normalizeReadinessEntries(entries, prefix) {
  if (!Array.isArray(entries)) {
    return []
  }

  return entries
    .map((entry, index) => {
      if (typeof entry === 'string') {
        return {
          code: `${prefix}_${index}`,
          field: null,
          message: entry,
        }
      }

      if (!entry || typeof entry !== 'object') {
        return null
      }

      const message = pickFirst(entry.message, entry.description, entry.label, entry.code, '')
      if (!message) {
        return null
      }

      return {
        code: pickFirst(entry.code, `${prefix}_${index}`),
        field: pickFirst(entry.field, null),
        message,
      }
    })
    .filter(Boolean)
}

function normalizeReadiness(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      isReady: true,
      blockingErrors: [],
      warnings: [],
      missingRequiredFields: [],
    }
  }

  const blockingErrors = normalizeReadinessEntries(raw.blockingErrors, 'BLOCKING')
  const warnings = normalizeReadinessEntries(raw.warnings, 'WARNING')
  const missingRequiredFields = normalizeReadinessEntries(raw.missingRequiredFields, 'MISSING')

  return {
    isReady: typeof raw.isReady === 'boolean' ? raw.isReady : blockingErrors.length === 0,
    blockingErrors,
    warnings,
    missingRequiredFields,
  }
}

function normalizeReadinessSummary(raw, readiness) {
  const normalizedReadiness = readiness || normalizeReadiness(null)
  if (!raw || typeof raw !== 'object') {
    return {
      isReady: normalizedReadiness.isReady,
      blockingErrorCount: normalizedReadiness.blockingErrors.length,
      warningCount: normalizedReadiness.warnings.length,
      missingRequiredCount: normalizedReadiness.missingRequiredFields.length,
      providerStatus: null,
    }
  }

  return {
    isReady: typeof raw.isReady === 'boolean' ? raw.isReady : normalizedReadiness.isReady,
    blockingErrorCount: toNumber(
      pickFirst(raw.blockingErrorCount, raw.blockingErrorsCount),
      normalizedReadiness.blockingErrors.length,
    ),
    warningCount: toNumber(
      pickFirst(raw.warningCount, raw.warningsCount),
      normalizedReadiness.warnings.length,
    ),
    missingRequiredCount: toNumber(
      pickFirst(raw.missingRequiredCount, raw.missingCount),
      normalizedReadiness.missingRequiredFields.length,
    ),
    providerStatus: pickFirst(raw.providerStatus, raw.taxStatus, null),
  }
}

function normalizeArtifact(raw) {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  return {
    type: pickFirst(raw.type, null),
    available: Boolean(raw.available),
    source: pickFirst(raw.source, 'none'),
    route: pickFirst(raw.route, raw.url, null),
    draftId: pickFirst(raw.draftId, raw.id, null),
    invoiceId: pickFirst(raw.invoiceId, null),
    invoiceNo: pickFirst(raw.invoiceNo, null),
    state: normalizeArtifactState(pickFirst(raw.state, null)),
    retryAfterSeconds: normalizeRetryAfterSeconds(
      pickFirst(raw.retryAfterSeconds, raw.retryAfter, null),
    ),
    lastCheckedAt: pickFirst(raw.lastCheckedAt, raw.checkedAt, null),
    keySearch: pickFirst(raw.keySearch, null),
    generatedAt: pickFirst(raw.generatedAt, null),
    issuedAt: pickFirst(raw.issuedAt, null),
    lookupUrl: pickFirst(raw.lookupUrl, null),
    lookupKey: pickFirst(raw.lookupKey, null),
  }
}

function normalizeArtifacts(rawArtifacts, singularArtifact) {
  const artifacts = rawArtifacts && typeof rawArtifacts === 'object' ? rawArtifacts : {}
  const normalized = {}

  if (artifacts.previewPdf) {
    normalized.previewPdf = normalizeArtifact(artifacts.previewPdf)
  }
  if (artifacts.issuedPdf) {
    normalized.issuedPdf = normalizeArtifact(artifacts.issuedPdf)
  }
  if (artifacts.xml) {
    normalized.xml = normalizeArtifact(artifacts.xml)
  }

  const artifact = normalizeArtifact(singularArtifact)
  if (!artifact) {
    return normalized
  }

  if (artifact.type === 'preview_pdf') {
    normalized.previewPdf = {
      ...(normalized.previewPdf || {}),
      ...artifact,
    }
  } else if (artifact.type === 'issued_pdf') {
    normalized.issuedPdf = {
      ...(normalized.issuedPdf || {}),
      ...artifact,
    }
  } else if (artifact.type === 'xml') {
    normalized.xml = {
      ...(normalized.xml || {}),
      ...artifact,
    }
  }

  return normalized
}

function normalizeSnapshot(raw) {
  if (!raw || typeof raw !== 'object' || !Object.keys(raw).length) {
    return null
  }

  const header = raw.header || {}
  const buyer = raw.buyer || {}
  const source = raw.source || {}
  const itemSource =
    typeof raw.itemSource === 'string'
      ? { strategy: raw.itemSource, available: true }
      : raw.itemSource || {}
  const amounts = raw.amounts || {}
  const resolvedItemCollection = resolveCanonicalItemCollection({ source: raw })
  const items = resolvedItemCollection.items.map((item) => normalizeLineItem(item))

  return {
    lane: pickFirst(raw.lane, null),
    source: {
      salesInvoiceId: pickFirst(source.salesInvoiceId, raw.salesInvoiceId, raw.billId, null),
      salesInvoiceType: pickFirst(source.salesInvoiceType, null),
    },
    header: {
      status: pickFirst(header.status, null),
      workState: pickFirst(header.workState, null),
      operationType: pickFirst(header.operationType, null),
      invoiceDate: pickFirst(header.invoiceDate, null),
      paymentMethod: pickFirst(header.paymentMethod, null),
      currency: pickFirst(header.currency, 'VND'),
      invoiceNo: pickFirst(header.invoiceNo, null),
      pattern: pickFirst(header.pattern, null),
      serial: pickFirst(header.serial, null),
      note: pickFirst(header.note, null),
    },
    buyer: {
      buyerName: pickFirst(buyer.buyerName, buyer.name, null),
      buyerTaxCode: pickFirst(buyer.buyerTaxCode, buyer.taxCode, null),
      buyerAddress: pickFirst(buyer.buyerAddress, buyer.address, null),
      buyerEmail: pickFirst(buyer.buyerEmail, buyer.email, null),
      buyerPhone: pickFirst(buyer.buyerPhone, buyer.phone, null),
    },
    itemSource: {
      strategy: pickFirst(itemSource.strategy, null),
      available: Boolean(itemSource.available),
    },
    items,
    amounts: {
      totalBeforeTax: toNumber(
        pickFirst(amounts.totalBeforeTax, amounts.totalAmountBeforeTax, amounts.subTotal),
      ),
      totalVat: toNumber(
        pickFirst(amounts.totalVat, amounts.vatAmount, amounts.totalVatAmount, amounts.taxAmount),
      ),
      totalAmount: toNumber(pickFirst(amounts.totalAmount, amounts.grandTotal)),
      totalDiscount: toNumber(pickFirst(amounts.totalDiscount, amounts.discountAmount)),
    },
  }
}

function normalizeRetailFacetBuyer(raw = {}) {
  const source = raw && typeof raw === 'object' ? raw : {}

  return {
    buyerName: normalizeNullableString(pickFirst(source.buyerName, source.name, null)),
    buyerTaxCode: normalizeNullableString(pickFirst(source.buyerTaxCode, source.taxCode, null)),
    buyerAddress: normalizeNullableString(pickFirst(source.buyerAddress, source.address, null)),
    buyerEmail: normalizeNullableString(pickFirst(source.buyerEmail, source.email, null)),
    buyerPhone: normalizeNullableString(pickFirst(source.buyerPhone, source.phone, null)),
  }
}

function normalizeFacetSnapshotMode(
  rawMode,
  { hasEInvoice = false, allowedActions = [], snapshot } = {},
) {
  const normalizedMode = typeof rawMode === 'string' ? rawMode.trim().toLowerCase() : ''
  if (normalizedMode === 'truth') {
    return 'truth'
  }
  if (normalizedMode === 'bootstrap_buyer_only') {
    return 'bootstrap_buyer_only'
  }
  if (normalizedMode === 'none') {
    return 'none'
  }

  if (hasEInvoice) {
    return 'truth'
  }

  const normalizedActions = Array.isArray(allowedActions)
    ? allowedActions.map((action) =>
        String(action || '')
          .trim()
          .toLowerCase(),
      )
    : []
  const hasBootstrapDraft = normalizedActions.includes('bootstrap-draft')
  const hasBuyerBootstrapSnapshot =
    snapshot &&
    typeof snapshot === 'object' &&
    (Object.values(snapshot.buyerCompany || {}).some((value) => value !== null) ||
      Object.values(snapshot.buyerIndividual || {}).some((value) => value !== null))

  if (hasBootstrapDraft && hasBuyerBootstrapSnapshot) {
    return 'bootstrap_buyer_only'
  }

  return 'none'
}

function normalizeFacetContractStatus(rawStatus) {
  if (typeof rawStatus !== 'string') {
    return null
  }

  const normalized = rawStatus.trim().toLowerCase()
  return normalized || null
}

function normalizeFacetContractError(rawError) {
  return rawError === undefined ? null : rawError
}

function canBootstrapDraftFromFacet(facet = {}) {
  const normalizedStatus = normalizeFacetContractStatus(
    pickFirst(facet?.facetStatus, facet?.facetMeta?.status, facet?.status, null),
  )
  const hasFacetMetaError = Boolean(
    facet?.facetMeta && Object.prototype.hasOwnProperty.call(facet.facetMeta, 'error'),
  )
  const facetError = Object.prototype.hasOwnProperty.call(facet || {}, 'facetError')
    ? facet.facetError
    : hasFacetMetaError
      ? facet.facetMeta.error
      : Object.prototype.hasOwnProperty.call(facet || {}, 'error')
        ? facet.error
        : null
  const snapshotMode = normalizeNullableString(
    pickFirst(facet?.snapshotMode, facet?.facetMeta?.snapshotMode, null),
  )
  const allowedActions = normalizeAllowedActions(facet?.allowedActions)

  return (
    normalizedStatus === 'ok' &&
    facetError === null &&
    snapshotMode === 'bootstrap_buyer_only' &&
    allowedActions.includes('bootstrap-draft')
  )
}

function normalizeRetailFacetSnapshot(raw) {
  if (!raw || typeof raw !== 'object' || !Object.keys(raw).length) {
    return {
      buyerCompany: normalizeRetailFacetBuyer(),
      buyerIndividual: normalizeRetailFacetBuyer(),
    }
  }

  const header = raw.header || {}
  const source = raw.source || {}
  const itemSource =
    typeof raw.itemSource === 'string'
      ? { strategy: raw.itemSource, available: true }
      : raw.itemSource || {}
  const amounts = raw.amounts || {}
  const resolvedItemCollection = resolveCanonicalItemCollection({ source: raw })
  const items = resolvedItemCollection.items.map((item) => normalizeLineItem(item))

  return {
    lane: pickFirst(raw.lane, null),
    source: {
      salesInvoiceId: pickFirst(source.salesInvoiceId, raw.salesInvoiceId, raw.billId, null),
      salesInvoiceType: pickFirst(source.salesInvoiceType, null),
    },
    header: {
      status: pickFirst(header.status, null),
      workState: pickFirst(header.workState, null),
      operationType: pickFirst(header.operationType, null),
      invoiceDate: pickFirst(header.invoiceDate, null),
      paymentMethod: pickFirst(header.paymentMethod, null),
      currency: pickFirst(header.currency, 'VND'),
      invoiceNo: pickFirst(header.invoiceNo, null),
      pattern: pickFirst(header.pattern, null),
      serial: pickFirst(header.serial, null),
      note: pickFirst(header.note, null),
    },
    buyerCompany: normalizeRetailFacetBuyer(raw.buyerCompany),
    buyerIndividual: normalizeRetailFacetBuyer(raw.buyerIndividual),
    itemSource: {
      strategy: pickFirst(itemSource.strategy, null),
      available: Boolean(itemSource.available),
    },
    items,
    amounts: {
      totalBeforeTax: toNumber(
        pickFirst(amounts.totalBeforeTax, amounts.totalAmountBeforeTax, amounts.subTotal),
      ),
      totalVat: toNumber(
        pickFirst(amounts.totalVat, amounts.vatAmount, amounts.totalVatAmount, amounts.taxAmount),
      ),
      totalAmount: toNumber(pickFirst(amounts.totalAmount, amounts.grandTotal)),
      totalDiscount: toNumber(pickFirst(amounts.totalDiscount, amounts.discountAmount)),
    },
  }
}

function normalizeCanonicalEInvoiceIdentity(raw) {
  if (!raw || typeof raw !== 'object' || !Object.keys(raw).length) {
    return null
  }

  return {
    ...raw,
    id: pickFirst(raw.id, null),
    billId: pickFirst(raw.billId, null),
    status: pickFirst(raw.status, null),
    workState: pickFirst(raw.workState, null),
    providerType: pickFirst(raw.providerType, null),
    operationType: pickFirst(raw.operationType, null),
    invoiceNo: pickFirst(raw.invoiceNo, null),
    invoiceKey: pickFirst(raw.invoiceKey, null),
    keySearch: pickFirst(raw.keySearch, null),
    pattern: pickFirst(raw.pattern, null),
    serial: pickFirst(raw.serial, null),
    verificationCode: pickFirst(raw.verificationCode, null),
    signedDate: pickFirst(raw.signedDate, null),
    issuedAt: pickFirst(raw.issuedAt, null),
    taxStatus: normalizeTaxStatus(raw.taxStatus, raw.workState),
    providerMeta:
      raw.providerMeta && typeof raw.providerMeta === 'object' ? raw.providerMeta : null,
    lastError: pickFirst(raw.lastError, null),
  }
}

function normalizeCanonicalSummary(raw) {
  return normalizeCanonicalReadModelSummary(raw, [])
}

function normalizeCanonicalReadModelSummary(
  raw,
  items = [],
  { strictSummaryContract = false, summaryPath = 'summary' } = {},
) {
  const source = raw && typeof raw === 'object' ? raw : null
  const normalizedItems = Array.isArray(items) ? items : []
  if (strictSummaryContract && !source) {
    throw createClientValidationError(
      'EINVOICE_CANONICAL_SUMMARY_REQUIRED',
      `Canonical detail thiếu summary bắt buộc tại ${summaryPath}`,
      { summaryPath },
    )
  }

  const totalBeforeTax = strictSummaryContract
    ? requireCanonicalSummaryField(source, 'totalBeforeTax', summaryPath)
    : toNumber(
        pickFirst(
          source?.totalBeforeTax,
          source?.totalAmountBeforeTax,
          source?.subTotal,
          source?.totalNet,
          source?.grandTotalNet,
          normalizedItems.reduce((sum, item) => sum + toNumber(item.totalBeforeTax), 0),
        ),
        0,
      )
  const totalVat = strictSummaryContract
    ? requireCanonicalSummaryField(source, 'totalVat', summaryPath)
    : toNumber(
        pickFirst(
          source?.totalVat,
          source?.vatAmount,
          source?.totalVatAmount,
          source?.taxAmount,
          source?.grandTotalVat,
          normalizedItems.reduce((sum, item) => sum + toNumber(item.taxAmount), 0),
        ),
        0,
      )
  const totalDiscount = strictSummaryContract
    ? requireCanonicalSummaryField(source, 'totalDiscount', summaryPath)
    : toNumber(
        pickFirst(
          source?.totalDiscount,
          source?.discountAmount,
          normalizedItems.reduce((sum, item) => sum + toNumber(item.discountAmount), 0),
        ),
        0,
      )
  const totalAmount = strictSummaryContract
    ? requireCanonicalSummaryField(source, 'totalAmount', summaryPath)
    : toNumber(
        pickFirst(
          source?.totalAmount,
          source?.grandTotal,
          source?.totalGross,
          source?.grandTotalGross,
          totalBeforeTax + totalVat,
        ),
        totalBeforeTax + totalVat,
      )
  const rawTaxBreakdown = Array.isArray(source?.taxBreakdown)
    ? source.taxBreakdown
    : Array.isArray(source?.vatBreakdown)
      ? source.vatBreakdown
      : []

  return {
    totalBeforeTax,
    totalVat,
    totalDiscount,
    totalAmount,
    amountInWords: pickFirst(source?.amountInWords, null),
    currency: pickFirst(source?.currency, 'VND'),
    taxBreakdown: rawTaxBreakdown.length
      ? rawTaxBreakdown.map((entry) => normalizeTaxBreakdownEntry(entry))
      : deriveTaxBreakdown(normalizedItems),
  }
}

function normalizeCanonicalReadModel({
  items,
  lineItems,
  convertedItems,
  source = null,
  summary,
  fallbackItems = [],
  fallbackSummary = null,
  strictItemContract = false,
  strictSummaryContract = false,
  itemCollectionPath = null,
  summaryPath = 'summary',
} = {}) {
  const resolvedItemCollection = resolveCanonicalItemCollection({
    items,
    lineItems,
    convertedItems,
    source,
    fallbackItems,
  })
  const collectionPath = itemCollectionPath || resolvedItemCollection.sourceKey || 'items'
  const normalizedItems = resolvedItemCollection.items.map((item, index) =>
    normalizeLineItem(item, {
      strictCanonicalMoneyBreakdown: strictItemContract,
      itemPath: `${collectionPath}[${index}]`,
    }),
  )
  return {
    items: normalizedItems,
    summary: normalizeCanonicalReadModelSummary(summary || fallbackSummary, normalizedItems, {
      strictSummaryContract,
      summaryPath,
    }),
  }
}

function normalizeCanonicalActionContext(raw, topLevelActions) {
  const source = raw && typeof raw === 'object' ? raw : {}
  const allowedActions = normalizeAllowedActions(
    Array.isArray(topLevelActions) && topLevelActions.length
      ? topLevelActions
      : source.allowedActions,
  )

  return {
    allowedActions,
    blockingReasons: Array.isArray(source.blockingReasons) ? source.blockingReasons : [],
    cancelFormType: pickFirst(source.cancelFormType, null),
    actionDescriptions:
      source.actionDescriptions && typeof source.actionDescriptions === 'object'
        ? source.actionDescriptions
        : {},
  }
}

function isCanonicalDetailPayload(item) {
  if (!item || typeof item !== 'object') {
    return false
  }

  return Boolean(
    item.salesInvoiceId &&
      (Object.prototype.hasOwnProperty.call(item, 'einvoice') ||
        Object.prototype.hasOwnProperty.call(item, 'snapshot') ||
        Object.prototype.hasOwnProperty.call(item, 'actionContext') ||
        Object.prototype.hasOwnProperty.call(item, 'summary')),
  )
}

function normalizeCanonicalInvoiceDetail(item = {}) {
  const snapshot = normalizeSnapshot(item.snapshot)
  const einvoice = normalizeCanonicalEInvoiceIdentity(item.einvoice)
  const actionContext = normalizeCanonicalActionContext(item.actionContext, item.allowedActions)
  const readiness = normalizeReadiness(item.readiness)
  const readinessSummary = normalizeReadinessSummary(item.readinessSummary, readiness)
  const artifacts = normalizeArtifacts(item.artifacts, null)
  const header = snapshot?.header || {}
  const buyer = snapshot?.buyer || {}
  const snapshotItemCollection = resolveCanonicalItemCollection({ source: item.snapshot })
  const readModel = normalizeCanonicalReadModel({
    source: item.snapshot,
    fallbackItems: [],
    summary: item.summary,
    fallbackSummary: null,
    strictItemContract: true,
    strictSummaryContract: true,
    itemCollectionPath: `snapshot.${snapshotItemCollection.sourceKey || 'items'}`,
    summaryPath: 'summary',
  })
  const lineItems = readModel.items
  const summary = readModel.summary
  const workState = pickFirst(item.workState, einvoice?.workState, null)
  const providerType = pickFirst(einvoice?.providerType, null)
  const billType = parseBillTypeItem(pickFirst(item.billType, einvoice?.billType, null))
  const billTypeName = normalizeNullableString(
    pickFirst(item.billTypeName, einvoice?.billTypeName, null),
  )
  const billMode = normalizeNullableInteger(pickFirst(item.billMode, null))
  const sourceChannel = normalizeSourceChannel(pickFirst(item.sourceChannel, null))
  const contractInvalid = Boolean(item.contractInvalid)
  const rawBlockingReasonsFromItem = Array.isArray(item.blockingReasons) ? item.blockingReasons : []
  const blockingReasons = appendContractBlockingReasons(
    [...rawBlockingReasonsFromItem, ...actionContext.blockingReasons],
    [],
  )

  return {
    ...item,
    id: pickFirst(einvoice?.id, null),
    salesInvoiceId: pickFirst(
      item.salesInvoiceId,
      snapshot?.source?.salesInvoiceId,
      einvoice?.billId,
      null,
    ),
    billNumber: Object.prototype.hasOwnProperty.call(item, 'billNumber')
      ? (item.billNumber ?? null)
      : null,
    billType,
    billTypeName,
    billMode,
    sourceChannel,
    contractInvalid,
    saleType: normalizeSaleType(snapshot?.lane),
    workState,
    status: normalizeStatus(pickFirst(einvoice?.status, workState)),
    taxStatus: normalizeTaxStatus(einvoice?.taxStatus, workState),
    providerType,
    providerMode: normalizeProviderMode(providerType),
    einvoice,
    snapshot,
    summary,
    allowedActions: contractInvalid ? [] : actionContext.allowedActions,
    blockingReasons,
    cancelFormType: actionContext.cancelFormType,
    actionContext,
    fieldSemantics: normalizeFieldSemantics(item.fieldSemantics),
    readiness,
    readinessSummary,
    normalizedPayloadHash: pickFirst(item.normalizedPayloadHash, null),
    artifacts,
    lineItems,
    buyerName: pickFirst(buyer.buyerName, null),
    buyerTaxCode: pickFirst(buyer.buyerTaxCode, null),
    buyerAddress: pickFirst(buyer.buyerAddress, null),
    buyerEmail: pickFirst(buyer.buyerEmail, null),
    buyerPhone: pickFirst(buyer.buyerPhone, null),
    buyerIdCard: null,
    invoiceDate: pickFirst(header.invoiceDate, null),
    invoiceNo: pickFirst(einvoice?.invoiceNo, header.invoiceNo, null),
    pattern: pickFirst(einvoice?.pattern, header.pattern, null),
    serial: pickFirst(einvoice?.serial, header.serial, null),
    paymentMethod: pickFirst(header.paymentMethod, null),
    operationType: pickFirst(einvoice?.operationType, header.operationType, null),
    voucherBook: null,
    totalBeforeTax: toNumber(pickFirst(summary?.totalBeforeTax, 0)),
    totalDiscount: toNumber(pickFirst(summary?.totalDiscount, 0)),
    totalVat: toNumber(pickFirst(summary?.totalVat, 0)),
    vatAmount: toNumber(pickFirst(summary?.totalVat, 0)),
    totalAmount: toNumber(pickFirst(summary?.totalAmount, 0)),
    amountInWords: pickFirst(summary?.amountInWords, null),
    currency: pickFirst(summary?.currency, header.currency, 'VND'),
    exchangeRate: 1,
    relations: Array.isArray(item.relations)
      ? item.relations.map((relation) => normalizeRelation(relation))
      : [],
    key: pickFirst(einvoice?.invoiceKey, null),
    keySearch: pickFirst(einvoice?.keySearch, null),
    lookupCode: pickFirst(einvoice?.keySearch, null),
    verificationCode: pickFirst(einvoice?.verificationCode, null),
    feedbackContent: pickFirst(item.feedbackContent, einvoice?.lastError, null),
    branchName: null,
    createdAt: null,
    issuedAt: pickFirst(einvoice?.issuedAt, null),
    finishedAt: pickFirst(einvoice?.issuedAt, null),
    taxCheckedAt: null,
    pdfFetchedAt: null,
    createdBy: null,
    pdfUrl: null,
    saleInvoiceCode: null,
    issueMode: null,
    adjustmentDraft: normalizeAdjustmentDraftSummary(item.adjustmentDraft),
  }
}

function normalizeAdjustmentDraftSummary(value) {
  if (!value || typeof value !== 'object') return null
  const id = value.id
  if (id == null) return null
  return {
    id,
    status: value.status ?? null,
    adjustmentInvoiceNo: value.adjustmentInvoiceNo ?? null,
  }
}

function normalizeRetailFacet(item = {}, fallbackSaleType = null) {
  const snapshot = normalizeRetailFacetSnapshot(item.snapshot)
  const rawEInvoiceRecord =
    item.einvoiceRecord && typeof item.einvoiceRecord === 'object'
      ? item.einvoiceRecord
      : item.einvoice && typeof item.einvoice === 'object'
        ? item.einvoice
        : null
  const einvoice = normalizeCanonicalEInvoiceIdentity(rawEInvoiceRecord)
  const actionContext = normalizeCanonicalActionContext(item.actionContext, item.allowedActions)
  const readiness = normalizeReadiness(item.readiness)
  const readinessSummary = normalizeReadinessSummary(item.readinessSummary, readiness)
  const artifacts = normalizeArtifacts(item.artifacts, null)
  const header = snapshot?.header || {}
  const workState = pickFirst(item.workState, einvoice?.workState, header.workState, null)
  const facetStatus = normalizeFacetContractStatus(pickFirst(item.facetStatus, item.status, null))
  const facetError = normalizeFacetContractError(
    Object.prototype.hasOwnProperty.call(item, 'error')
      ? item.error
      : Object.prototype.hasOwnProperty.call(item, 'facetError')
        ? item.facetError
        : null,
  )
  const providerType = pickFirst(einvoice?.providerType, item.providerType, item.providerMode, null)
  const hasEInvoice = Boolean(rawEInvoiceRecord)
  const snapshotMode = normalizeFacetSnapshotMode(item.snapshotMode, {
    hasEInvoice,
    allowedActions: actionContext.allowedActions,
    snapshot,
  })

  return {
    ...item,
    id: pickFirst(einvoice?.id, item.id, item.invoiceId, null),
    salesInvoiceId: normalizeNullableString(
      pickFirst(
        item.salesInvoiceId,
        item.billId,
        item.salesInvoiceID,
        snapshot?.source?.salesInvoiceId,
      ),
    ),
    saleType: normalizeSaleType(
      pickFirst(item.saleType, snapshot?.lane, item.type, fallbackSaleType),
    ),
    workState,
    status: normalizeStatus(pickFirst(einvoice?.status, workState)),
    taxStatus: normalizeTaxStatus(pickFirst(einvoice?.taxStatus, item.taxStatus, null), workState),
    facetStatus,
    facetError,
    facetMeta: {
      status: facetStatus,
      error: facetError,
      snapshotMode,
    },
    providerType,
    providerMode: normalizeProviderMode(providerType),
    einvoice,
    einvoiceRecord: rawEInvoiceRecord,
    hasEInvoice,
    snapshotMode,
    snapshot,
    buyerCompany: snapshot.buyerCompany,
    buyerIndividual: snapshot.buyerIndividual,
    allowedActions: actionContext.allowedActions,
    blockingReasons: actionContext.blockingReasons,
    cancelFormType: actionContext.cancelFormType,
    actionContext,
    fieldSemantics: normalizeFieldSemantics(item.fieldSemantics),
    readiness,
    readinessSummary,
    normalizedPayloadHash: pickFirst(item.normalizedPayloadHash, null),
    artifacts,
    invoiceDate: pickFirst(header.invoiceDate, null),
    invoiceNo: pickFirst(einvoice?.invoiceNo, header.invoiceNo, null),
    pattern: pickFirst(einvoice?.pattern, header.pattern, null),
    serial: pickFirst(einvoice?.serial, header.serial, null),
    paymentMethod: pickFirst(header.paymentMethod, null),
    operationType: pickFirst(einvoice?.operationType, header.operationType, null),
    feedbackContent: pickFirst(item.feedbackContent, einvoice?.lastError, null),
  }
}

const INVALID_BILL_TYPE_CONTRACT_ERROR = Object.freeze({
  code: 'INVALID_BILL_TYPE',
  message: 'Loại hóa đơn không hợp lệ',
})

function normalizeListBillTypeContract(item, record, { strictBillTypeContract = true } = {}) {
  const billType = parseBillTypeItem(pickFirst(item.billType, record.billType, null))
  const saleTypeByBillType = resolveSaleTypeByBillType(billType)
  const backendContractInvalid = Boolean(item.contractInvalid || record.contractInvalid)
  const invalidBillType = strictBillTypeContract && !saleTypeByBillType
  const isContractInvalid = backendContractInvalid || invalidBillType
  const backendBillTypeName = normalizeNullableString(
    pickFirst(item.billTypeName, record.billTypeName, null),
  )

  return {
    billType: saleTypeByBillType ? billType : null,
    billTypeName: backendBillTypeName,
    contractErrors: invalidBillType ? [{ ...INVALID_BILL_TYPE_CONTRACT_ERROR }] : [],
    contractInvalid: isContractInvalid,
    saleTypeByBillType,
  }
}

function appendContractBlockingReasons(blockingReasons = [], contractErrors = []) {
  const seen = new Set()
  const merged = []

  ;[...blockingReasons, ...contractErrors].forEach((reason) => {
    if (!reason || typeof reason !== 'object') {
      return
    }

    const code = normalizeNullableString(reason.code)
    if (code && seen.has(code)) {
      return
    }

    if (code) {
      seen.add(code)
    }

    merged.push(reason)
  })

  return merged
}

function normalizeInvoiceListItem(item = {}, options = {}) {
  const record = item.__preferSelf ? item : item.draft || item.einvoice || item
  const einvoice = item.einvoice && typeof item.einvoice === 'object' ? item.einvoice : {}
  const header = record.header || {}
  const buyer = record.buyer || {}
  const summary = record.summary || {}
  const actionContext = item.actionContext || {}
  const readiness = normalizeReadiness(pickFirst(item.readiness, record.readiness, null))
  const readinessSummary = normalizeReadinessSummary(
    pickFirst(item.readinessSummary, record.readinessSummary, null),
    readiness,
  )
  const snapshot = normalizeSnapshot(pickFirst(item.snapshot, record.snapshot, null))
  const fieldSemantics = normalizeFieldSemantics(
    pickFirst(item.fieldSemantics, record.fieldSemantics, null),
  )
  const artifacts = normalizeArtifacts(
    pickFirst(item.artifacts, record.artifacts, null),
    pickFirst(item.artifact, record.artifact, null),
  )
  const normalizedStatus = normalizeStatus(
    pickFirst(
      item.workState,
      record.workState,
      record.status,
      record.invoiceStatus,
      record.lifecycleStatus,
    ),
  )
  const billTypeContract = normalizeListBillTypeContract(item, record, options)
  const legacySaleType = normalizeSaleType(
    pickFirst(
      item.saleType,
      record.saleType,
      snapshot?.lane,
      record.providerMode,
      record.providerType,
      record.type,
    ),
  )
  const saleType =
    options.strictBillTypeContract === false
      ? billTypeContract.saleTypeByBillType || legacySaleType
      : billTypeContract.saleTypeByBillType
  const billMode = normalizeNullableInteger(pickFirst(item.billMode, record.billMode, null))
  const sourceChannel = normalizeSourceChannel(pickFirst(item.sourceChannel, record.sourceChannel, null))
  const rawBlockingReasonsFromActionContext = Array.isArray(actionContext.blockingReasons)
    ? actionContext.blockingReasons
    : []
  const rawBlockingReasonsFromItem = Array.isArray(item.blockingReasons)
    ? item.blockingReasons
    : []
  const rawBlockingReasonsFromRecord = Array.isArray(record.blockingReasons)
    ? record.blockingReasons
    : []
  const rawBlockingReasons = appendContractBlockingReasons(
    [...rawBlockingReasonsFromItem, ...rawBlockingReasonsFromRecord, ...rawBlockingReasonsFromActionContext],
    [],
  )
  const blockingReasons = appendContractBlockingReasons(
    rawBlockingReasons,
    billTypeContract.contractErrors,
  )

  const viewModel = {
    ...record,
    ...item,
    id: pickFirst(record.id, record.draftId, record.invoiceId, einvoice.id),
    salesInvoiceId: pickFirst(
      item.salesInvoiceId,
      record.salesInvoiceId,
      record.billId,
      record.saleInvoiceId,
      snapshot?.source?.salesInvoiceId,
      null,
    ),
    invoiceNo: pickFirst(
      record.invoiceNo,
      einvoice.invoiceNo,
      header.invoiceNo,
      record.invoiceNumber,
      record.number,
      null,
    ),
    pattern: pickFirst(
      record.pattern,
      einvoice.pattern,
      header.templateCode,
      record.templateCode,
      record.formCode,
      null,
    ),
    serial: pickFirst(
      record.serial,
      einvoice.serial,
      header.serial,
      record.series,
      record.symbol,
      null,
    ),
    saleInvoiceCode: pickFirst(
      item.billNumber,
      record.saleInvoiceCode,
      record.billNumber,
      record.billId,
      record.billCode,
      record.saleInvoice?.code,
      record.saleBillCode,
      null,
    ),
    billType: billTypeContract.billType,
    billTypeName: billTypeContract.billTypeName,
    billMode,
    sourceChannel,
    contractErrors: billTypeContract.contractErrors,
    contractInvalid: billTypeContract.contractInvalid,
    saleType,
    // buyerName = "Người mua hàng" (người đại diện đi mua) — KHÔNG fallback về customerName.
    // Khi user không nhập, để null để FE không hiển thị nhầm tên khách hàng vào "Người mua".
    buyerName: pickFirst(
      record.buyerName,
      item.buyerName,
      snapshot?.buyer?.buyerName,
      null,
    ),
    buyerTaxCode: pickFirst(
      record.buyerTaxCode,
      item.buyerTaxCode,
      record.customerTaxCode,
      item.customerTaxCode,
      record.taxCode,
      item.taxCode,
      snapshot?.buyer?.buyerTaxCode,
      buyer.taxCode,
      record.customer?.taxCode,
      item.customer?.taxCode,
      null,
    ),
    buyerPhone: pickFirst(
      record.buyerPhone,
      item.buyerPhone,
      record.customerPhone,
      item.customerPhone,
      record.phone,
      item.phone,
      snapshot?.buyer?.buyerPhone,
      buyer.phone,
      record.customer?.phone,
      item.customer?.phone,
      null,
    ),
    buyerIdCard: pickFirst(
      record.buyerIdCard,
      record.customerIdCard,
      record.customerIdNumber,
      buyer.idCard,
      record.customer?.idCard,
      null,
    ),
    buyerAddress: pickFirst(
      record.buyerAddress,
      item.buyerAddress,
      record.customerAddress,
      item.customerAddress,
      record.address,
      item.address,
      snapshot?.buyer?.buyerAddress,
      buyer.address,
      record.customer?.address,
      item.customer?.address,
      null,
    ),
    buyerEmail: pickFirst(
      record.buyerEmail,
      item.buyerEmail,
      record.customerEmail,
      item.customerEmail,
      record.email,
      item.email,
      snapshot?.buyer?.buyerEmail,
      buyer.email,
      record.customer?.email,
      item.customer?.email,
      null,
    ),
    totalAmount: toNumber(
      pickFirst(
        record.totalAmount,
        item.totalAmount,
        summary.totalAmount,
        summary.grandTotal,
        record.amount,
        item.amount,
        record.grandTotal,
      ),
    ),
    totalBeforeTax: toNumber(
      pickFirst(
        record.totalBeforeTax,
        item.totalBeforeTax,
        summary.totalBeforeTax,
        summary.subTotal,
        record.amount,
        item.amount,
        record.subTotal,
      ),
    ),
    totalDiscount: toNumber(
      pickFirst(
        record.totalDiscount,
        item.totalDiscount,
        summary.discountAmount,
        summary.totalDiscount,
        record.discountAmount,
        item.discountAmount,
        record.discountTotal,
      ),
    ),
    totalVat: toNumber(
      pickFirst(
        record.totalVat,
        item.totalVat,
        summary.totalVat,
        summary.vatAmount,
        record.vatAmount,
        item.vatAmount,
        record.taxAmount,
        item.taxAmount,
        record.totalTax,
      ),
    ),
    vatAmount: toNumber(
      pickFirst(
        record.vatAmount,
        item.vatAmount,
        summary.vatAmount,
        summary.totalVat,
        record.taxAmount,
        item.taxAmount,
        record.totalTax,
      ),
    ),
    invoiceDate: pickFirst(
      record.invoiceDate,
      header.date,
      record.issuedDate,
      record.createdAt,
      null,
    ),
    issuedAt: pickFirst(
      record.issuedAt,
      einvoice.issuedAt,
      artifacts?.issuedAt,
      null,
    ),
    workState: pickFirst(item.workState, record.workState, einvoice.workState, null),
    status: normalizedStatus,
    taxStatus: normalizeTaxStatus(
      pickFirst(einvoice.taxStatus, record.taxStatus),
      pickFirst(record.taxResultStatus, item.workState, einvoice.workState, einvoice.status),
    ),
    feedbackContent: pickFirst(
      record.feedbackContent,
      record.taxFeedback,
      record.taxMessage,
      record.feedback,
      einvoice.lastError,
      record.lastError,
      null,
    ),
    providerMode: normalizeProviderMode(
      pickFirst(
        record.providerMode,
        record.providerType,
        einvoice.providerType,
        record.provider?.mode,
      ),
    ),
    key: pickFirst(record.key, record.providerKey, record.invoiceKey, einvoice.invoiceKey, null),
    keySearch: pickFirst(record.keySearch, record.searchKey, einvoice.keySearch, null),
    verificationCode: pickFirst(
      record.verificationCode,
      record.taxVerificationCode,
      record.verification?.code,
      einvoice.verificationCode,
      null,
    ),
    voucherBook: pickFirst(record.voucherBook, header.voucherBook, null),
    paymentMethod: pickFirst(record.paymentMethod, header.paymentMethod, null),
    operationType: pickFirst(
      record.operationType,
      einvoice.operationType,
      header.operationType,
      null,
    ),
    issueMode: pickFirst(record.issueMode, null),
    amountInWords: pickFirst(record.amountInWords, summary.amountInWords, null),
    currency: pickFirst(record.currency, summary.currency, 'VND'),
    exchangeRate: toNumber(pickFirst(record.exchangeRate, summary.exchangeRate), 1),
    storeName: pickFirst(
      record.storeName,
      item.storeName,
      record.store?.name,
      item.store?.name,
      null,
    ),
    companyName: pickFirst(
      record.companyName,
      item.companyName,
      record.company?.name,
      item.company?.name,
      null,
    ),
    pdfUrl: pickFirst(record.issuedPdfUrl, record.previewPdfUrl, null) || null,
    allowedActions: billTypeContract.contractInvalid
      ? []
      : normalizeAllowedActions(pickFirst(item.allowedActions, actionContext.allowedActions, [])),
    blockingReasons,
    cancelFormType: pickFirst(item.cancelFormType, actionContext.cancelFormType, null),
    einvoice: Object.keys(einvoice).length ? einvoice : null,
    snapshot,
    fieldSemantics,
    readiness,
    readinessSummary,
    normalizedPayloadHash: pickFirst(
      item.normalizedPayloadHash,
      record.normalizedPayloadHash,
      null,
    ),
    artifacts,
    adjustmentDraft: normalizeAdjustmentDraftSummary(item.adjustmentDraft),
  }

  if (options.strictBillTypeContract === false) {
    return viewModel
  }

  const { providerType, providerMode, ...strictViewModel } = viewModel
  void providerType
  void providerMode
  return strictViewModel
}

function normalizeLegacyInvoiceDetail(item = {}) {
  const source = item && typeof item === 'object' ? item : {}
  const isDraftWrapped = source.draft && typeof source.draft === 'object'
  const record = isDraftWrapped ? source.draft : source
  const header = record.header || {}
  const buyer = record.buyer || {}
  const base = normalizeInvoiceListItem(
    isDraftWrapped ? source : { ...source, __preferSelf: true },
    { strictBillTypeContract: false },
  )
  const resolvedItemCollection = resolveCanonicalItemCollection({
    items: Array.isArray(record.items) ? record.items : undefined,
    lineItems: Array.isArray(record.lineItems) ? record.lineItems : undefined,
    convertedItems: Array.isArray(record.convertedItems) ? record.convertedItems : undefined,
    source: record,
    fallbackItems: Array.isArray(record.invoiceItems) ? record.invoiceItems : [],
  })
  const lineItems = resolvedItemCollection.items.map((lineItem) => normalizeLineItem(lineItem))
  const fallbackSummary = {
    totalBeforeTax: pickFirst(
      record.totalBeforeTax,
      record.totalAmountBeforeTax,
      record.subTotal,
      source.totalBeforeTax,
      source.totalAmountBeforeTax,
      source.subTotal,
    ),
    totalVat: pickFirst(
      record.totalVat,
      record.vatAmount,
      record.totalVatAmount,
      record.taxAmount,
      source.totalVat,
      source.vatAmount,
      source.totalVatAmount,
      source.taxAmount,
    ),
    totalDiscount: pickFirst(
      record.totalDiscount,
      record.discountAmount,
      record.discountTotal,
      source.totalDiscount,
      source.discountAmount,
      source.discountTotal,
    ),
    totalAmount: pickFirst(
      record.totalAmount,
      record.grandTotal,
      source.totalAmount,
      source.grandTotal,
    ),
    amountInWords: pickFirst(record.amountInWords, source.amountInWords, null),
    currency: pickFirst(record.currency, source.currency, 'VND'),
    exchangeRate: pickFirst(record.exchangeRate, source.exchangeRate, 1),
    taxBreakdown: pickFirst(record.taxBreakdown, source.taxBreakdown, null),
  }
  const summarySource =
    record.summary && typeof record.summary === 'object' ? record.summary : fallbackSummary
  const summary = normalizeCanonicalReadModelSummary(summarySource, lineItems)
  const relations = (
    Array.isArray(record.relations)
      ? record.relations
      : Array.isArray(record.relatedInvoices)
        ? record.relatedInvoices
        : Array.isArray(source.relatedInvoices)
          ? source.relatedInvoices
          : []
  ).map((relation) => normalizeRelation(relation))

  return {
    ...base,
    lineItems,
    items: lineItems,
    summary,
    relations,
    branchName: pickFirst(
      base.branchName,
      record.branchName,
      record.branch?.name,
      source.branchName,
      source.branch?.name,
      null,
    ),
    buyerName: pickFirst(base.buyerName, buyer.buyerName, buyer.name, null),
    buyerTaxCode: pickFirst(base.buyerTaxCode, buyer.buyerTaxCode, buyer.taxCode, null),
    buyerAddress: pickFirst(base.buyerAddress, buyer.buyerAddress, buyer.address, null),
    buyerEmail: pickFirst(base.buyerEmail, buyer.buyerEmail, buyer.email, null),
    buyerPhone: pickFirst(base.buyerPhone, buyer.buyerPhone, buyer.phone, null),
    buyerIdCard: pickFirst(base.buyerIdCard, buyer.buyerIdCard, buyer.idCard, null),
    invoiceDate: pickFirst(base.invoiceDate, header.date, header.invoiceDate, null),
    voucherBook: pickFirst(base.voucherBook, header.voucherBook, null),
    paymentMethod: pickFirst(base.paymentMethod, header.paymentMethod, null),
    operationType: pickFirst(base.operationType, header.operationType, null),
    totalBeforeTax: toNumber(pickFirst(base.totalBeforeTax, summary.totalBeforeTax), 0),
    totalDiscount: toNumber(pickFirst(base.totalDiscount, summary.totalDiscount), 0),
    totalVat: toNumber(pickFirst(base.totalVat, summary.totalVat), 0),
    vatAmount: toNumber(pickFirst(base.vatAmount, summary.totalVat), 0),
    totalAmount: toNumber(pickFirst(base.totalAmount, summary.totalAmount), 0),
    amountInWords: pickFirst(base.amountInWords, summary.amountInWords, null),
    currency: pickFirst(base.currency, summary.currency, 'VND'),
    exchangeRate: toNumber(pickFirst(base.exchangeRate, fallbackSummary.exchangeRate, 1), 1),
  }
}

function normalizeInvoiceDetail(item = {}) {
  if (isCanonicalDetailPayload(item)) {
    return normalizeCanonicalInvoiceDetail(item)
  }

  return normalizeLegacyInvoiceDetail(item)
}

function normalizeInvoiceListResponse(payload = {}) {
  const source = payload?.data || payload
  const items = Array.isArray(source?.items) ? source.items : []

  return {
    items: items.map((item) => normalizeInvoiceListItem(item)),
    total: toNumber(pickFirst(source?.total, source?.totalItems, items.length)),
    totalPages: toNumber(pickFirst(source?.totalPages, source?.pages, 0)),
  }
}


function normalizePreviewPdfPayload(payload = {}) {
  const source = payload?.data || payload
  if (source instanceof Blob) {
    throw createClientValidationError(
      'EINVOICE_PREVIEW_PDF_CONTRACT_INVALID',
      'Phản hồi preview PDF không hợp lệ: thiếu pdfBase64',
      { expected: 'response.data.pdfBase64 (non-empty string)', actual: 'Blob' },
    )
  }

  const rawPdfBase64 = typeof source?.pdfBase64 === 'string' ? source.pdfBase64.trim() : ''
  if (!rawPdfBase64) {
    throw createClientValidationError(
      'EINVOICE_PREVIEW_PDF_CONTRACT_INVALID',
      'Phản hồi preview PDF không hợp lệ: thiếu pdfBase64',
      { expected: 'response.data.pdfBase64 (non-empty string)' },
    )
  }

  return {
    blob: null,
    pdfBase64: rawPdfBase64,
    url: null,
  }
}

function normalizeIssuedPdfPayload(payload = {}) {
  const source = payload?.data || payload
  if (source instanceof Blob) {
    return { blob: source, pdfBase64: null, url: null }
  }

  return {
    blob: source?.blob || null,
    pdfBase64: pickFirst(source?.pdfBase64, source?.base64, source?.content, null) || null,
    url: pickFirst(source?.pdfUrl, source?.url, null) || null,
  }
}

function normalizePdfPayload(payload = {}) {
  return normalizeIssuedPdfPayload(payload)
}

function adaptDeleteDraftResponse(response) {
  return {
    deletedRecordId: response?.deletedRecordId ?? null,
    deletedLinkId: response?.deletedLinkId ?? null,
    deletedAt: response?.deletedAt ?? null,
  }
}

export {
  adaptDeleteDraftResponse,
  buildBulkIssueSubmitFingerprint,
  canBootstrapDraftFromFacet,
  normalizeBulkIssueProgressResponse,
  normalizeBulkIssueSubmitRequest,
  normalizeBulkIssueSubmitResponse,
  normalizeBulkPrecheckRequest,
  normalizeBulkPrecheckResponse,
  normalizeArtifacts,
  normalizeCanonicalReadModel,
  normalizeFieldSemantics,
  normalizeInvoiceDetail,
  normalizeInvoiceListItem,
  normalizeInvoiceListResponse,
  normalizeLineItem,
  normalizePdfPayload,
  normalizePreviewPdfPayload,
  normalizeIssuedPdfPayload,
  normalizeReadiness,
  normalizeReadinessSummary,
  normalizeRetailFacet,
  normalizeSnapshot,
  normalizeStatus,
  normalizeTaxStatus,
}
